import React from 'react'

import styled, { css } from 'styled-components'

import { pxToEm, pxToVw } from 'src/functions/units'

import Icon from '../Icon'

/*
 * Copyright (c) 2022 automata-network/witness-v2 authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
const faqs = [
  {
    sectionTitle: 'Donation payment methods',
    questions: [
      {
        q: 'How can I donate my money?',
        a: 'We accept donations in three ways: 1) PayNow QR code, 2) Bank transfer and 3) Crypto transfer.'
      },
      {
        q: 'Can I donate without a crypto wallet?',
        a: 'Yes, you can proceed with your donation via PayNow or Bank transfer.'
      },
      {
        q: 'Is there a requirement regarding the donation amount?',
        a: (
          <>
            No, but if you are to donate more than 20,000 SGD, please contact us first for further
            assistance:{' '}
            <a href="mailto:Doinggood@thegoodblockchain.sg" target="_blank" rel="noreferrer">
              Doinggood@thegoodblockchain.sg
            </a>
          </>
        )
      }
    ]
  },
  {
    sectionTitle: 'STAMP tokens and NFTs',
    questions: [
      {
        q: 'Can I redeem the NFTs without a crypto wallet?',
        a: (
          <>
            No. You will need to connect your crypto wallet to receive STAMP tokens, which will be
            used to redeem NFTs. If you do not have a MetaMask wallet and wish to set up yours,
            click{' '}
            <a
              href="https://metamask.zendesk.com/hc/en-us/articles/360015489531-Getting-started-with-MetaMask"
              target="_blank"
              rel="noreferrer"
            >
              here
            </a>{' '}
            for the guide.
          </>
        )
      },
      {
        q: 'What is the STAMP token used for?',
        a: 'STAMP token is an ERC-20 token exclusively used to redeem NFTs in the National Day 2022 NFT Fundraising Campaign. STAMP tokens will only be valid between 8th Aug to 21st Aug 2022.'
      },
      {
        q: 'How many STAMP tokens will I get?',
        a: 'You will get 1 STAMP token for every 1 SGD donation.'
      },
      {
        q: 'Can I donate without participating in the NFT campaign?',
        a: 'Yes, you can untoggle the “Participate in the NFT campaign” button and you will not be required to connect a crypto wallet.'
      }
    ]
  },
  {
    sectionTitle: 'Tax deduction',
    questions: [
      {
        q: 'How to receive tax deduction?',
        a: 'Donors who donate above 200 SGD are eligible for tax deduction. To receive tax deduction, you will need to provide your (i) name/company name, (ii) NRIC / UEN; (iii) email address; and (iv) phone number. They will be given to SG Enable for processing your tax deduction.'
      }
    ]
  },
  {
    sectionTitle: 'Choice of crypto wallet',
    questions: [
      {
        q: 'What crypto wallet can I use?',
        a: 'For this campaign, we only support metamask.'
      }
    ]
  },
  {
    sectionTitle: 'Others',
    questions: [
      {
        q: 'Is there an official site for the campaign?',
        a: (
          <>
            Yes, please check{' '}
            <a href="https://thegoodblockchain.sg/" target="_blank" rel="noreferrer">
              https://thegoodblockchain.sg/
            </a>
            .
          </>
        )
      },
      {
        q: 'What is a campaign NFT?',
        a: 'All the donors who donate beyond 200 SGD can receive a campaign NFT, on the condition that the crypto wallet is connected to the site.'
      }
    ]
  }
]

const Faqs = () => {
  return (
    <>
      <StyledTips>
        If you are new to the crypto world, we understand that embarking on the Web3 journey isn't
        hassle-free, still, we encourage you to try it out and are happy to assist you along the
        way. Read the following FAQs, which will help you navigate in an easier way.
      </StyledTips>
      {faqs.map((item) => {
        return (
          <React.Fragment key={item.sectionTitle}>
            <StyledSectionTitle>{item.sectionTitle}</StyledSectionTitle>
            {item.questions.map((item) => {
              return (
                <React.Fragment key={item.q}>
                  <StyledQuestion>
                    <StyledQuestionIcon icon="faq-question-decoration" size={26} color="#DBA369" />
                    {item.q}
                  </StyledQuestion>
                  <StyledAnswer>{item.a}</StyledAnswer>
                </React.Fragment>
              )
            })}
          </React.Fragment>
        )
      })}
      <StyledTips>We reserve the right for the final interpretation.</StyledTips>
    </>
  )
}

export default Faqs

const StyledSectionTitle = styled.div`
  display: flex;
  align-items: center;
  color: #969696;
  font-weight: 500;
  font-size: ${pxToVw(14)};
  gap: ${pxToVw(15)};
  margin-top: ${pxToVw(40)};
  margin-bottom: ${pxToVw(40)};

  &::before,
  &::after {
    flex-grow: 1;
    content: '';
    height: 1px;
    background-color: #ededed;
  }

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(14, 720)};
      gap: ${pxToVw(15, 720)};
      margin-top: ${pxToVw(40, 720)};
      margin-bottom: ${pxToVw(40, 720)};
    `}
  `}
`

const StyledQuestion = styled.div`
  color: #969696;
  font-weight: 500;
  line-height: ${pxToEm(28, 24)};
  font-size: ${pxToVw(24)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(24, 720)};
    `}
  `}
`

const StyledAnswer = styled.div`
  color: #a7a7a7;
  line-height: ${pxToEm(23, 18)};
  font-size: ${pxToVw(18)};
  margin-top: ${pxToVw(10)};
  margin-bottom: ${pxToVw(40)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      font-size: ${pxToVw(18, 720)};
      margin-top: ${pxToVw(10, 720)};
      margin-bottom: ${pxToVw(40, 720)};
    `}
  `}
`

const StyledTips = styled.div`
  color: #a7a7a7;
  line-height: ${pxToEm(23, 18)};
  font-size: ${pxToVw(18)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
    font-size: ${pxToVw(18, 720)};
  `}
  `}
`

const StyledQuestionIcon = styled(Icon)`
  vertical-align: text-top;
  margin-right: ${pxToVw(12)};

  ${({ theme }) => css`
    ${theme.mediaQuery.mobile`
      margin-right: ${pxToVw(12, 720)};
    `}
  `}
`
